import { IUPAC_PRIMER } from '../../../Util/orderSettingDetection/iupac.ts'
import type {
  ExtraColumnTarget,
  SheetMapping,
  SheetMetadataField,
  SheetRef,
  SheetState
} from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { CSVFieldType } from '../../../View/Page/HomePage/Modals/CSVModalSelector/CsvModalAISelector.ts'
import { identifyCSVSuggestion } from '../../../View/Page/HomePage/Modals/CSVModalSelector/CsvModalAISelector.ts'
import { baseNameOf, sampleStemOf } from '../../../Util/fileNames.ts'
import { sequenceIdFits } from '../../../Util/SampleFileUtils.ts'
import { parseCoordinate, readCoordinates } from './sheetImport.ts'
import { detectTypeColumn, judgePoolColumn, poolHeaderNoun } from '../../../Util/sheetItemRoles/index.ts'
import { isControlFileName, isControlSampleName } from '../negativeControls.ts'

/**
 * Which sheet column plays which role, scored on what each column holds (primer-like DNA, short tags, file
 * names, names matching the dropped files), headers only breaking ties. The classic import goes by header
 * words alone and takes i5/i7 index columns for primers; its matchers still pick the metadata fields.
 */

export type MappingContext = {
  /** The dropped FASTQ file names, which file and sample-id columns are recognised against. */
  fileNames: readonly string[]
}

const DEGENERATE_BASE = /[RYSWKMBDHV]/
const SHORT_TAG = /^[ACGTN]{4,16}$/
const FASTQ_NAME = /\.(fastq|fq)(\.gz)?$/i

const normalizeHeader = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, '')
const words = (header: string) =>
  header
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
const cleanDna = (value: string) => value.toUpperCase().replace(/[\s-]/g, '')
const baseName = (value: string) => (value.split(/[\\/]/).pop() ?? value).trim().toLowerCase()

type ColumnProfile = {
  header: string
  values: string[]
  primerShare: number
  degenerateShare: number
  tagShare: number
  fileShare: number
  stemShare: number
  distinctShare: number
}

const share = (values: readonly string[], test: (value: string) => boolean) =>
  values.length === 0 ? 0 : values.filter(test).length / values.length

export const profileColumns = (
  headers: readonly string[],
  rows: readonly Record<string, string>[],
  context: MappingContext
): ColumnProfile[] => {
  const fileNames = new Set(context.fileNames.map(baseName))
  const stems = new Set(context.fileNames.map((name) => sampleStemOf(name)))
  return headers.map((header) => {
    const values = rows.map((row) => (row[header] ?? '').trim()).filter((value) => value !== '')
    return {
      header,
      values,
      primerShare: share(values, (v) => IUPAC_PRIMER.test(cleanDna(v))),
      degenerateShare: share(values, (v) => IUPAC_PRIMER.test(cleanDna(v)) && DEGENERATE_BASE.test(cleanDna(v))),
      tagShare: share(values, (v) => SHORT_TAG.test(cleanDna(v))),
      fileShare: share(values, (v) => FASTQ_NAME.test(v) || fileNames.has(baseName(v))),
      stemShare: share(values, (v) => stems.has(sampleStemOf(v))),
      distinctShare: values.length === 0 ? 0 : new Set(values).size / values.length
    }
  })
}

const SIDE_FORWARD = ['forward', 'fwd', 'f', 'r1', 'read1', 'left', 'fw', '5']
const SIDE_REVERSE = ['reverse', 'rev', 'r', 'r2', 'read2', 'right', 'rv', '3']

const sideOf = (header: string): 'forward' | 'reverse' | null => {
  const tokens = words(header)
  if (tokens.some((t) => SIDE_FORWARD.includes(t)) || /(^|[^a-z])(fwd|forward)/i.test(header)) {
    return 'forward'
  }
  if (tokens.some((t) => SIDE_REVERSE.includes(t)) || /(^|[^a-z])(rev|reverse)/i.test(header)) {
    return 'reverse'
  }
  return null
}

/** Two columns of one kind: the headers say which side each is, else the sheet's column order does. */
const splitSides = (columns: readonly string[]): { forward?: string; reverse?: string } => {
  if (columns.length === 0) {
    return {}
  }
  if (columns.length === 1) {
    return sideOf(columns[0]) === 'reverse' ? { reverse: columns[0] } : { forward: columns[0] }
  }
  const forward = columns.find((c) => sideOf(c) === 'forward') ?? columns.find((c) => sideOf(c) !== 'reverse') ?? columns[0]
  const reverse = columns.find((c) => c !== forward && sideOf(c) === 'reverse') ?? columns.find((c) => c !== forward)
  return { forward, reverse }
}

const INDEX_HEADER = /(^|[^a-z])(i5|i7|index)([^a-z]|$)/i
const TAG_HEADER = /(tag|barcode|bc)/i
const SAMPLE_NAME_HEADERS = [
  'samplename',
  'customersamplename',
  'customersample',
  'sample',
  'name',
  'probe',
  'probenname',
  'probename',
  'label',
  'samplelabel',
  'probenbezeichnung',
  'bezeichnung'
]
const CUSTOMER_WORD = /customer|client|kunde/
const SAMPLE_WORD = /sample|probe|bezeichnung|name|id/
/**
 * Exact headers in list order, then any naming the customer with a sample, a name or an id (`Kundenprobenname`,
 * `Client sample ID`). `Customer` or `Kunde` alone is the customer organisation.
 */
const sampleNameRank = (header: string): number | null => {
  const normalized = normalizeHeader(header)
  const exact = SAMPLE_NAME_HEADERS.indexOf(normalized)
  if (exact >= 0) {
    return exact
  }
  return CUSTOMER_WORD.test(normalized) && SAMPLE_WORD.test(normalized.replace(CUSTOMER_WORD, '')) ? SAMPLE_NAME_HEADERS.length : null
}
const SAMPLE_ID_HEADERS = ['sampleid', 'sequenceid', 'sequencingid', 'seqid', 'libraryid', 'library', 'id']

const METADATA_FIELDS: SheetMetadataField[] = [
  'coordinatesMerged',
  'coordinatesLat',
  'coordinatesLon',
  'samplingDate',
  'samplingStart',
  'samplingEnd',
  'matrixType',
  'extractionKit',
  'trapType',
  'collectionType',
  'sequencer',
  'sequencingChemistry',
  'sequencingDepth',
  'localityType',
  'habitatType'
]

const inRange = (limit: 90 | 180) => (value: string) => parseCoordinate(value.replace(',', '.'), limit) !== undefined
const COORDINATE_CHECKS: Partial<Record<SheetMetadataField, { header: RegExp; value: (value: string) => boolean }>> = {
  coordinatesLat: { header: /^(lat|latitude|decimallatitude)$/, value: inRange(90) },
  coordinatesLon: { header: /^(lon|lng|long|longitude|decimallongitude)$/, value: inRange(180) },
  coordinatesMerged: {
    header: /^(coordinates?|coords?|location|gps)$/,
    value: (v) => readCoordinates({ v }, { coordinatesMerged: 'v' }).kind !== 'invalid'
  }
}

/*
 * The classic coordinate matchers accept any header containing "lat" (so "Plate") and their value
 * validators never reject, because `find(x => x === false)` finds a falsy `false`. Coordinates are
 * therefore checked here: a whole header word, and most values in range. Not all: one "NA" or DMS cell
 * would otherwise drop the whole column silently, where mapped it is reported per row.
 */
const coordinateColumn = (
  field: SheetMetadataField,
  headers: readonly string[],
  rows: readonly Record<string, string>[]
): string | undefined => {
  const check = COORDINATE_CHECKS[field]
  if (!check) {
    return undefined
  }
  return headers.find((header) => {
    const values = rows.map((row) => row[header] ?? '').filter((v) => v.trim() !== '')
    return words(header).some((word) => check.header.test(word)) && values.filter(check.value).length * 2 > values.length
  })
}

/** The classic matchers, with headers compared as `samplingdate` so "Sampling date" and "sampling_date" count. */
const metadataColumn = (
  field: SheetMetadataField,
  headers: readonly string[],
  rows: readonly Record<string, string>[]
): string | undefined => {
  if (field in COORDINATE_CHECKS) {
    return coordinateColumn(field, headers, rows)
  }
  const classic = identifyCSVSuggestion(field as CSVFieldType, [...headers], [...rows])
  if (classic) {
    return classic[0]
  }
  const wanted = normalizeHeader(field)
  return headers.find((header) => normalizeHeader(header) === wanted)
}

/**
 * A sequence-id column (by its header, as either import reads it) whose values are mostly file-name
 * prefixes, as the classic import matches them; a value fitting several samples does not count.
 */
const prefixIdColumn = (
  profiles: readonly ColumnProfile[],
  rows: readonly Record<string, string>[],
  context: MappingContext
): ColumnProfile | undefined => {
  const fits = (value: string) => new Set(context.fileNames.filter((name) => sequenceIdFits(value, baseNameOf(name))).map(sampleStemOf)).size === 1
  return profiles.find(
    (p) =>
      (SAMPLE_ID_HEADERS.includes(normalizeHeader(p.header)) || !!identifyCSVSuggestion('sequenceId', [p.header], [...rows])) &&
      share(p.values, fits) >= 0.5
  )
}

export const mapSheetColumns = (
  headers: readonly string[],
  rows: readonly Record<string, string>[],
  context: MappingContext
): SheetMapping => {
  const profiles = profileColumns(headers, rows, context)
  const taken = new Set<string>()
  const reasons: Record<string, string> = {}
  const take = (column: string | undefined, reason: string) => {
    if (column) {
      taken.add(column)
      reasons[column] = reason
    }
    return column
  }
  const free = (profile: ColumnProfile) => !taken.has(profile.header) && profile.values.length > 0

  // Illumina indexes never carry degenerate bases, so an i5/i7 column that does holds (tagged) primers.
  const primerColumns = profiles
    .filter((p) => free(p) && p.primerShare >= 0.8 && (!INDEX_HEADER.test(p.header) || p.degenerateShare >= 0.8))
    .map((p) => p.header)
  const primers = splitSides(primerColumns.slice(0, 2))
  const forwardPrimer = take(primers.forward, 'primer sequences (12+ IUPAC bases)')
  const reversePrimer = take(primers.reverse, 'primer sequences (12+ IUPAC bases)')

  const fileColumns = profiles.filter((p) => free(p) && p.fileShare >= 0.5).map((p) => p.header)
  const files = splitSides(fileColumns.slice(0, 2))
  const fileForward = take(files.forward, 'FASTQ file names')
  const fileReverse = take(files.reverse, 'FASTQ file names')

  const tagColumns = profiles
    .filter((p) => free(p) && p.tagShare >= 0.8 && TAG_HEADER.test(p.header) && !INDEX_HEADER.test(p.header))
    .map((p) => p.header)
  const tags = splitSides(tagColumns.slice(0, 2))
  const forwardTag = take(tags.forward, 'short DNA tags')
  const reverseTag = take(tags.reverse, 'short DNA tags')

  const idCandidates = profiles.filter((p) => free(p) && p.stemShare >= 0.5).sort((a, b) => b.stemShare - a.stemShare)
  const byIdHeader = profiles.find((p) => free(p) && SAMPLE_ID_HEADERS.includes(normalizeHeader(p.header)) && p.stemShare > 0)
  const byPrefix = byIdHeader || idCandidates[0] ? undefined : prefixIdColumn(profiles.filter(free), rows, context)
  const sampleId =
    take((idCandidates[0] ?? byIdHeader)?.header, 'values match the dropped files') ??
    take(byPrefix?.header, 'values are prefixes of the dropped file names')

  const nameCandidates = profiles.filter(
    (p) => free(p) && p.distinctShare >= 0.9 && p.primerShare < 0.5 && p.tagShare < 0.5 && sampleNameRank(p.header) !== null
  )
  const classicName = identifyCSVSuggestion('customerSample', [...headers], [...rows])?.[0]
  const nameColumn =
    (classicName && !taken.has(classicName) ? classicName : undefined) ??
    nameCandidates.sort((a, b) => (sampleNameRank(a.header) ?? 0) - (sampleNameRank(b.header) ?? 0))[0]?.header
  const sampleName = take(nameColumn, 'customer sample names')

  const typeColumn = detectTypeColumn(
    headers.filter((h) => !taken.has(h)),
    rows
  )
  const itemType = take(typeColumn?.column, `sample types: ${typeColumn?.reason ?? ''}`)
  /* without a type column, the names say which rows are controls, as they label the samples */
  const isControl = (row: number) => {
    const role = typeColumn?.roles[row]
    if (role) {
      return role === 'NEGATIVE_CONTROL'
    }
    const name = sampleName ? rows[row]?.[sampleName] : undefined
    const file = fileForward ? rows[row]?.[fileForward] : undefined
    return (!!name && isControlSampleName(name)) || (!!file && isControlFileName(file))
  }
  /* a column that fails the control rule is still mapped: the pools question then offers it with the reason */
  const poolCandidates = headers
    .filter((h) => !taken.has(h) && poolHeaderNoun(h))
    .map((h) => judgePoolColumn(h, rows, { isControl, itemKey: (row) => `row-${row}` }))
    .filter((detection) => detection.usable && detection.verdict !== 'single')
  const poolColumn = poolCandidates.find((detection) => detection.auto) ?? poolCandidates[0]
  const pool = take(poolColumn?.column, `pools: ${poolColumn?.reason ?? ''}`)

  const metadata: SheetMapping['metadata'] = {}
  for (const field of METADATA_FIELDS) {
    const column = metadataColumn(
      field,
      headers.filter((h) => !taken.has(h)),
      rows
    )
    if (column) {
      metadata[field] = take(column, `metadata: ${field}`)
    }
  }
  if (metadata.coordinatesMerged || Boolean(metadata.coordinatesLat) !== Boolean(metadata.coordinatesLon)) {
    for (const field of ['coordinatesLat', 'coordinatesLon'] as const) {
      const column = metadata[field]
      if (column) {
        delete metadata[field]
        taken.delete(column)
        delete reasons[column]
      }
    }
  }

  return {
    fileForward,
    fileReverse,
    sampleId,
    sampleName,
    forwardPrimer,
    reversePrimer,
    forwardTag,
    reverseTag,
    itemType,
    pool,
    metadata,
    reasons
  }
}

/** A sheet can only be matched when it names the files or the samples. */
export const hasKeyColumn = (mapping: SheetMapping) =>
  !!(mapping.fileForward || mapping.fileReverse || mapping.sampleId || mapping.sampleName)

export const unmappedColumns = (headers: readonly string[], mapping: SheetMapping): string[] =>
  headers.filter((header) => !(header in mapping.reasons))

/** A freshly read sheet: every column the mapping leaves unclaimed is kept as a custom attribute until the user says otherwise. */
export const newSheetState = (
  file: { key: string; name: string },
  parsed: { headers: string[]; rows: Record<string, string>[]; notes: string[] },
  context: MappingContext
): SheetState => {
  const mapping = mapSheetColumns(parsed.headers, parsed.rows, context)
  const extraColumns: Record<string, ExtraColumnTarget> = Object.fromEntries(
    unmappedColumns(parsed.headers, mapping).map((column) => [column, 'custom'])
  )
  return {
    fileKey: file.key,
    name: file.name,
    headers: parsed.headers,
    rows: parsed.rows,
    notes: parsed.notes,
    mapping,
    extraColumns,
    revision: 0
  }
}

/** Identifies what a written item received from the sheet: the file and the column choices at the time. */
export const sheetRevision = (sheet: SheetState): string => `${sheet.fileKey}#${sheet.revision}`

/** The columns that pick a sample's row and its primers. */
const PRIMER_ROLES = [
  'sampleId',
  'sampleName',
  'fileForward',
  'fileReverse',
  'forwardPrimer',
  'reversePrimer',
  'forwardTag',
  'reverseTag'
] as const

/** Every sample-level role: those, and the sample's type and pool. */
export const SHEET_ROLES = [...PRIMER_ROLES, 'itemType', 'pool'] as const

export type SheetRole = (typeof SHEET_ROLES)[number]

/**
 * Identifies what a primer check read from the sheet: the file and the columns that pick a sample's row
 * and its primers. Unlike `sheetRevision`, a metadata column choice leaves it alone, so a check (and its
 * telemetry) is not repeated for it.
 */
export const primerRevision = (sheet: SheetState): string =>
  [sheet.fileKey, ...PRIMER_ROLES.map((role) => sheet.mapping[role] ?? '')].join('\u0000')

export const METADATA_FIELD_OPTIONS: readonly SheetMetadataField[] = METADATA_FIELDS

const mappedColumns = (mapping: SheetMapping): string[] =>
  [
    mapping.fileForward,
    mapping.fileReverse,
    mapping.sampleId,
    mapping.sampleName,
    mapping.forwardPrimer,
    mapping.reversePrimer,
    mapping.forwardTag,
    mapping.reverseTag,
    mapping.itemType,
    mapping.pool,
    ...Object.values(mapping.metadata)
  ].filter((column): column is string => !!column)

/**
 * A sheet dropped again after a reload gets back the column choices made before, as long as its columns
 * are still there; otherwise it is mapped afresh.
 */
export const restoreSheetChoices = (sheet: SheetState, ref: SheetRef | undefined): SheetState => {
  if (!ref || ref.fileKey !== sheet.fileKey || !mappedColumns(ref.mapping).every((column) => sheet.headers.includes(column))) {
    return sheet
  }
  const extraColumns = Object.fromEntries(
    unmappedColumns(sheet.headers, ref.mapping).map((column) => [column, ref.extraColumns[column] ?? 'custom'])
  )
  return { ...sheet, mapping: ref.mapping, extraColumns, revision: ref.revision }
}

/** What one sheet column feeds: a sample field, a metadata field, a custom attribute, or nothing. */
export type ColumnTarget = SheetRole | ExtraColumnTarget

const isRole = (target: ColumnTarget): target is SheetRole => (SHEET_ROLES as readonly string[]).includes(target)
const isField = (target: ColumnTarget): target is SheetMetadataField => (METADATA_FIELDS as readonly string[]).includes(target)
/** A target only one column can feed. */
const isExclusive = (target: ColumnTarget) => isRole(target) || isField(target)

/** Each column's target as the sheet has it now; a column neither mapped nor kept is not imported. */
export const columnTargets = (sheet: Pick<SheetState, 'headers' | 'mapping' | 'extraColumns'>): Record<string, ColumnTarget> =>
  Object.fromEntries(
    sheet.headers.map((header) => {
      const role = SHEET_ROLES.find((candidate) => sheet.mapping[candidate] === header)
      const field = METADATA_FIELDS.find((candidate) => sheet.mapping.metadata[candidate] === header)
      return [header, role ?? field ?? sheet.extraColumns[header] ?? 'drop'] as const
    })
  )

/** `column` pointed at `target`; a column that fed that field before is no longer imported, rather than kept under another name. */
export const retarget = (targets: Record<string, ColumnTarget>, column: string, target: ColumnTarget): Record<string, ColumnTarget> =>
  Object.fromEntries(
    Object.entries(targets).map(([other, current]) => [
      other,
      other === column ? target : isExclusive(target) && current === target ? 'drop' : current
    ])
  )

const CHOSEN = 'chosen by you'

/**
 * The sheet with every column pointed at its target, written again as a new revision. A field claimed
 * twice goes to the first column; the others are not imported.
 */
export const withColumnTargets = (sheet: SheetState, targets: Record<string, ColumnTarget>): SheetState => {
  const before = columnTargets(sheet)
  const mapping: SheetMapping = { metadata: {}, reasons: {} }
  const extraColumns: Record<string, ExtraColumnTarget> = {}
  const claimed = new Set<ColumnTarget>()
  for (const header of sheet.headers) {
    const wanted = targets[header] ?? before[header]
    const target = isExclusive(wanted) && claimed.has(wanted) ? 'drop' : wanted
    if (isRole(target)) {
      mapping[target] = header
    } else if (isField(target)) {
      mapping.metadata[target] = header
    } else {
      extraColumns[header] = target
      continue
    }
    claimed.add(target)
    mapping.reasons[header] = before[header] === target ? (sheet.mapping.reasons[header] ?? CHOSEN) : CHOSEN
  }
  return { ...sheet, mapping, extraColumns, revision: sheet.revision + 1 }
}
